import { getSubscription } from '../services/apiSubscriptionNew';
import { getStore } from '../services/serviceProvider/apiStore';
import { syncOnboardingFlagsFromStore } from './onboardingRoute';

function stripSubscription(store) {
  if (!store || typeof store !== 'object') return {};
  const next = { ...store };
  delete next.current_subscription;
  delete next.subscription;
  delete next.active_subscription;
  delete next.subscription_history;
  return next;
}

/**
 * Read `/vendor/subscription` (or an equivalent body) into current + history.
 */
export function extractSubscription(payload) {
  if (!payload || typeof payload !== 'object') {
    return { current: null, history: [] };
  }

  const inner =
    payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)
      ? payload.data
      : payload;

  if (
    Object.prototype.hasOwnProperty.call(inner, 'current_subscription') ||
    inner.history ||
    inner.subscription_history
  ) {
    return {
      current: inner.current_subscription ?? null,
      history: Array.isArray(inner.history)
        ? inner.history
        : Array.isArray(inner.subscription_history)
          ? inner.subscription_history
          : [],
    };
  }

  if (inner.package_id || inner.id || inner.package_type || inner.is_trial != null) {
    return { current: inner, history: [] };
  }

  return { current: null, history: [] };
}

export function mergeApiSubscription(store, subscriptionBody) {
  const { current, history } = extractSubscription(subscriptionBody);
  const base = stripSubscription(store);
  return {
    ...base,
    current_subscription: current,
    subscription_history: history,
  };
}

/**
 * Load store + subscription for vendor onboarding.
 * Subscription fields always come from `/vendor/subscription`.
 * A successful store response replaces cached username.
 * If that request fails, the previous username is kept so an existing
 * merchant is not sent through onboarding again during an outage.
 */
export async function loadVendorOnboardingState(user) {
  if (!user || (user.role !== 'business' && user.role !== 'mall')) {
    return user;
  }

  const storeId =
    user.store_id ||
    user.store?.id ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('store-id') : null);

  const cachedUsername = user?.store?.username || '';
  let store = stripSubscription(user.store);
  delete store.username;
  let storeConfirmed = false;

  if (storeId && storeId !== 'undefined' && storeId !== 'null') {
    try {
      const storeRes = await getStore(storeId);
      const storeData = storeRes?.data?.store || storeRes?.store || null;
      if (storeData && typeof storeData === 'object') {
        store = stripSubscription(storeData);
        storeConfirmed = true;
      }
    } catch (err) {
      console.warn('[auth] store refresh failed:', err);
    }
  }

  // A failed store request may keep the last confirmed username so an existing
  // merchant is not sent back through onboarding during an outage.
  // A successful response with no username always wins over the cache.
  if (!storeConfirmed && cachedUsername) {
    store = { ...store, username: cachedUsername };
  }

  try {
    const payload = await getSubscription();
    store = mergeApiSubscription(store, payload);
  } catch (err) {
    const status = err?.response?.status;
    store = {
      ...stripSubscription(store),
      current_subscription: null,
      subscription_history: [],
    };
    if (status !== 404 && status !== 204) {
      console.warn('[auth] subscription refresh failed:', err);
    }
  }

  syncOnboardingFlagsFromStore(store);

  return {
    ...user,
    store_id: store.id || user.store_id || storeId,
    store,
  };
}
